// Radar del Centro V3 — capa de datos (solo lectura, joins en frontend para DEMO INTERNA).
// Atribución de telemetría a persona: se usa EXCLUSIVAMENTE el persona_id que resuelve el backend
// en /ekh/current-state (cascada explícita->cama->zona). Si persona_id es null => dato de habitación/zona.
// Nunca se infiere identidad, ni "en cama", ni "normal/estable/riesgo".
import { esTecnica, verboEvento, acentoPersona } from "@/lib/consolaV3/meta";
import { CAPACIDAD_LABEL } from "@/lib/alarmMeta";

export function haceTexto(iso, nowMs) {
  if (!iso) return null;
  const s = Math.max(0, Math.floor((nowMs - new Date(iso).getTime()) / 1000));
  if (s < 60) return `hace ${s} s`;
  if (s < 3600) return `hace ${Math.floor(s / 60)} min`;
  if (s < 86400) return `hace ${Math.floor(s / 3600)} h`;
  return `hace ${Math.floor(s / 86400)} d`;
}

// Cronómetro de alarma HH:MM:SS o N d HH h.
export function relojAlarma(iso, nowMs) {
  if (!iso) return "—";
  let s = Math.max(0, Math.floor((nowMs - new Date(iso).getTime()) / 1000));
  if (s >= 86400) { const d = Math.floor(s / 86400); const h = Math.floor((s % 86400) / 3600); return `${d} d ${String(h).padStart(2, "0")} h`; }
  const hh = Math.floor(s / 3600); s %= 3600; const mm = Math.floor(s / 60); const ss = s % 60;
  return hh > 0 ? `${hh}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}` : `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

// Estados REALES que emite GET /api/cobertura (routes_v2.cobertura_tecnica):
// protegida | perdida | no_confirmable | sin_cobertura. `en_riesgo` NO existe en el backend y se
// admite como alias heredado en etiquetas y filtro (COB_REVISAR) por compatibilidad con
// fixtures/demo; los datos reales de /api/cobertura nunca lo emiten.
export const COBERTURA_MAP = {
  protegida: { label: "Cobertura confirmada", tone: "ok" },
  perdida: { label: "Cobertura interrumpida", tone: "bad" },
  no_confirmable: { label: "Cobertura no confirmable", tone: "muted" },
  sin_cobertura: { label: "Sin cobertura configurada para esta capacidad", tone: "muted" },
  en_riesgo: { label: "Cobertura degradada", tone: "warn" },   // alias heredado (demo/fixtures)
};
export const mapCobertura = (estado) => COBERTURA_MAP[estado] || { label: "Cobertura no confirmable", tone: "muted" };

// CRITERIO ÚNICO de "a revisar" para marcas, listado y contador: cobertura interrumpida
// (caída confirmada) o no confirmable (sin evidencia). `protegida` y `sin_cobertura` conservan su
// tratamiento vigente y NO se presentan como advertencia. No crea alarmas ni prioridades.
const COB_REVISAR = ["perdida", "no_confirmable", "en_riesgo"];
export const esCoberturaRevisar = (estado) => COB_REVISAR.includes(estado);
const rankCobertura = (estado) => (estado === "perdida" ? 0 : estado === "en_riesgo" ? 1 : 2);
export const capLabel = (c) => CAPACIDAD_LABEL[c] || c;

// Un dispositivo aporta telemetría (presencia/vitales) si es EKH/EKT (bearer).
export function esTelemetria(d) {
  const m = (d.modelo || "").toUpperCase(); const t = (d.tipo || "").toUpperCase();
  return m.includes("EKH") || m.includes("EKT") || m.includes("DS-TDSB00") || t.includes("EKH") || t.includes("EKT") || !!d.usa_transporte_bearer;
}

// Texto de presencia/movimiento a partir del current_state (sin interpretar estados humanos).
export function presenciaDe(cs) {
  if (!cs) return null;
  const ts = cs.last_update || cs.last_seen;
  if (cs.movement === "moving") return { texto: "Movimiento detectado", ts };
  if (cs.presence === true || cs.person_exist_status === "existence") return { texto: "Persona detectada", ts };
  if (cs.presence === false || cs.person_exist_status === "inexistence") return { texto: "Persona no detectada", ts };
  return null;
}

// Vitales válidos: numéricos > 0 y presencia válida y atribución unívoca (persona_id).
export function vitalesDe(cs) {
  if (!cs || !cs.persona_id) return null;
  const presente = cs.presence === true || cs.person_exist_status === "existence";
  if (!presente) return null;
  const hr = cs.heart_rate, rr = cs.respiratory_rate;
  const hrOk = Number.isFinite(hr) && hr > 0;
  const rrOk = Number.isFinite(rr) && rr > 0;
  if (!hrOk && !rrOk) return null;
  return { hr: hrOk ? hr : null, resp: rrOk ? rr : null, ts: cs.last_update || cs.last_seen };
}

export function estadoAtencion(a) {
  if (a.estado === "recibida") return "sin_tomar";
  return "en_atencion";
}

// V6 · NOMBRES: el DTO externo autorizado publica `nombre`; el consumidor interno compatible
// conserva `nombre_completo`. Antes sólo se leía `nombre_completo`, así que una cuenta
// institucional veía «—» en representación, iniciales, búsqueda y orden. No se modifica ningún
// registro ni se amplía la allowlist del DTO: sólo se lee el campo autorizado que ya viaja.
export const nombrePersona = (p) => (p?.nombre || p?.nombre_completo || "").trim();

// Construye la VISTA del centro (mismo shape que consumen los fixtures) desde endpoints reales.
// `fallos` = { csFail, cobFail }: RADAR-002. Tras un error de consulta la última lectura se
// CONSERVA pero se identifica como histórica ("última lectura sin confirmar"); nunca se sigue
// afirmando vigente ni se inventa una caída. Sin timestamps, TTL ni motor de frescura nuevos.
export function construirVista(detalle, alarmas, currentStates, coberturaAll, nowMs, fallos = {}) {
  const csFail = !!fallos.csFail;
  const cobFail = !!fallos.cobFail;
  const sufijoHist = " · última lectura sin confirmar";
  const sitio = detalle.sitio || {};
  const sid = sitio.id;
  const personas = detalle.personas || [];
  const zonas = detalle.zonas || [];
  const dispositivos = detalle.dispositivos || [];
  const zonaNombre = Object.fromEntries(zonas.map((z) => [z.id, z.nombre]));
  const devById = Object.fromEntries(dispositivos.map((d) => [d.id, d]));

  const alarmasSitio = (alarmas || []).filter((a) => a.sitio_id === sid && a.estado !== "cerrada");
  const cobSitio = (coberturaAll || []).filter((c) => c.sitio_id === sid);
  const csSitio = (currentStates || []).filter((c) => c.sitio_id === sid);

  // último current_state por persona (atribuido)
  const csByPersona = {};
  for (const cs of csSitio) {
    if (!cs.persona_id) continue;
    const prev = csByPersona[cs.persona_id];
    if (!prev || new Date(cs.last_update || cs.last_seen) > new Date(prev.last_update || prev.last_seen)) csByPersona[cs.persona_id] = cs;
  }
  // current_state a nivel habitación (persona_id null) agrupado por zona del dispositivo
  const roomInfoByZona = {};
  for (const cs of csSitio) {
    if (cs.persona_id) continue;
    const dev = devById[cs.device_id];
    const zid = dev?.zona_id || "__nozona__";
    const p = presenciaDe(cs); if (!p) continue;
    (roomInfoByZona[zid] = roomInfoByZona[zid] || []).push(p);
  }
  // personas con capacidad de telemetría
  const capable = new Set();
  for (const d of dispositivos) if (esTelemetria(d)) for (const pid of (d.personas_monitoreadas || [])) capable.add(pid);

  // cobertura por persona
  const cobByPersona = {};
  for (const c of cobSitio) (cobByPersona[c.persona_id] = cobByPersona[c.persona_id] || []).push(c);

  const roomOf = (p) => p.habitacion || zonaNombre[p.zona_id] || "";
  const zonaKeyOf = (p) => zonaNombre[p.zona_id] || (p.habitacion || "Sin zona configurada");

  // alarmas humanas por persona (para tarjeta). RADAR-005: una persona puede tener VARIAS activas;
  // la principal se elige con el orden YA vigente (mayor prioridad; a igualdad, la más antigua).
  const alarmasByPersona = {};
  for (const a of alarmasSitio) if (!esTecnica(a) && a.persona_id) (alarmasByPersona[a.persona_id] = alarmasByPersona[a.persona_id] || []).push(a);
  const alarmaByPersona = {};
  for (const [pid, lista] of Object.entries(alarmasByPersona)) {
    alarmaByPersona[pid] = [...lista].sort((x, y) => rankPrioridad(x.prioridad) - rankPrioridad(y.prioridad)
      || new Date(x.hora_recepcion_servidor) - new Date(y.hora_recepcion_servidor))[0];
  }

  // ¿cuántas personas comparten cada zona?
  const personasPorZona = {};
  for (const p of personas) { const k = zonaKeyOf(p); (personasPorZona[k] = personasPorZona[k] || []).push(p); }

  const personaVista = (p) => {
    const cs = csByPersona[p.id];
    const cobs = cobByPersona[p.id] || [];
    const degr = cobs.filter((c) => esCoberturaRevisar(c.estado))
      .sort((a, b) => rankCobertura(a.estado) - rankCobertura(b.estado));
    const marca = degr.length ? (() => {
      const m = mapCobertura(degr[0].estado);
      const que = m.label.replace(/^Cobertura /, "");
      return { label: `Cobertura de ${capLabel(degr[0].capacidad)}: ${que}${cobFail ? sufijoHist : ""}`,
               tone: cobFail ? "muted" : m.tone, estado: degr[0].estado, historica: cobFail };
    })() : null;
    const pres = presenciaDe(cs);
    const vit = vitalesDe(cs);
    const compartida = (personasPorZona[zonaKeyOf(p)] || []).length > 1;
    let observabilidad;
    if (cs) observabilidad = "lectura";
    else if (capable.has(p.id)) observabilidad = "sin_lectura";
    else observabilidad = "sin_sensor";
    const al = alarmaByPersona[p.id];
    return {
      id: p.id, nombre: nombrePersona(p) || "—", room: roomOf(p),
      alarma: al ? { id: al.id, alertasActivas: (alarmasByPersona[p.id] || []).length, verbo: verboEvento(al.tipo), tipo: al.tipo, prioridad: normPri(al.prioridad), hace: relojAlarma(al.hora_recepcion_servidor, nowMs), estado: estadoAtencion(al), responsable: al.estado === "recibida" ? null : "Central Galarya", operador: al.operador_nombre || null, ekhEvento: al.ekh_evento || null } : null,
      presencia: pres ? { texto: pres.texto, hace: haceTexto(pres.ts, nowMs) } : null,
      vitals: vit ? { hr: vit.hr, resp: vit.resp, hace: haceTexto(vit.ts, nowMs) } : null,
      cama: "no_disponible",
      observabilidad, coberturaMarca: marca, compartida, lecturaHistorica: csFail && !!cs,
    };
  };

  // agrupar por zona (orden espacial estable por nombre de zona, luego persona)
  const zonasOrden = [...new Set(personas.map(zonaKeyOf))].sort((a, b) => a.localeCompare(b, "es"));
  const grupos = zonasOrden.map((zn) => {
    const ps = personas.filter((p) => zonaKeyOf(p) === zn).sort((a, b) => nombrePersona(a).localeCompare(nombrePersona(b), "es"));
    const zid = ps[0] ? (ps[0].zona_id || "__nozona__") : "__nozona__";
    return { nombre: zn, personas: ps.map(personaVista), salaInfo: (roomInfoByZona[zid] || []).map((p) => ({ texto: p.texto, hace: haceTexto(p.ts, nowMs) })) };
  });

  const atencion = alarmasSitio.filter((a) => !esTecnica(a)).map((a) => ({
    id: a.id, verbo: verboEvento(a.tipo), persona: a.persona_nombre || "Persona", room: a.zona_nombre || "",
    prioridad: normPri(a.prioridad), hace: relojAlarma(a.hora_recepcion_servidor, nowMs), estado: estadoAtencion(a),
    responsable: a.estado === "recibida" ? null : "Central Galarya", operador: a.operador_nombre || null,
    ekhEvento: a.ekh_evento || null,
  })).sort((x, y) => rankPrioridad(x.prioridad) - rankPrioridad(y.prioridad) || 0);

  const coberturas = cobSitio.filter((c) => esCoberturaRevisar(c.estado))
    .sort((a, b) => rankCobertura(a.estado) - rankCobertura(b.estado)
      || (a.persona_nombre || "").localeCompare(b.persona_nombre || "", "es"))
    .map((c) => {
      const m = mapCobertura(c.estado);
      return { persona: c.persona_nombre, capacidad: capLabel(c.capacidad), estado: c.estado,
               label: cobFail ? `${m.label}${sufijoHist}` : m.label, tone: cobFail ? "muted" : m.tone };
    });

  return {
    sitio: { nombre: sitio.nombre, direccion: sitio.direccion, ciudad: sitio.ciudad },
    header: {
      total: personas.length,
      requieren: atencion.filter((a) => a.estado === "sin_tomar").length,
      enAtencion: atencion.filter((a) => a.estado !== "sin_tomar").length,
      coberturasRevisar: coberturas.length,
      coberturasHistoricas: cobFail,
    },
    atencion, grupos, coberturas, coberturasHistoricas: cobFail, lecturasHistoricas: csFail,
  };
}

export function normPri(p) { return p === "maxima" ? "alta" : (p || "media"); }
export function rankPrioridad(p) { const n = normPri(p); return n === "alta" ? 0 : n === "media" ? 1 : 2; }
export const acentoPri = (p) => acentoPersona(normPri(p));
